import {
  NotifiedPartyEnum,
  type SafeguardingEnquiryOutcomeEnum
} from '@lmc/validations'
import { Form as AntdForm, DatePicker, Input, Select } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import kebabCase from 'lodash/kebabCase'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import IncidentCaseV2StepForm from '../IncidentCaseV2StepForm'
import type {
  IncidentCaseV2StepProps,
  IncidentCaseV2StepValues
} from '../incidentCaseV2Steps'
import StepCheckbox from '../StepCheckbox'
import StepField from '../StepField'
import { StepReveal, StepRevealGroup } from '../StepReveal'
import { DATE_FORMAT, StepSection } from '../stepLayout'
import { useTeamMemberOptions } from '../stepOptions'
import {
  fromDateOnlyIso,
  hasNotifiedPartyDetail,
  type NotifiedPartyValue,
  savedNotifiedParties,
  toDateOnlyIso,
  trimmed,
  withoutBlanks
} from '../stepValues'
import SafeguardingEnquiryFields, {
  NO_ENQUIRY
} from './notifications/SafeguardingEnquiryFields'

const testId = 'incident-case-v2-notifications'

type PartyDefinition = {
  party: NotifiedPartyEnum
  labelKey: string
  dateLabelKey: string
  byLabelKey: string
  hasReferenceNumber?: boolean
  hasEnquiry?: boolean
}

const NOTIFIED = {
  byLabelKey: incidentReportsConstants.CASE_V2_NOTIFICATIONS_NOTIFIED_BY,
  dateLabelKey: incidentReportsConstants.CASE_V2_NOTIFICATIONS_NOTIFIED_DATE
}

/** The parties this step records, in design order, with the detail each takes. */
export const PARTIES: ReadonlyArray<PartyDefinition> = [
  {
    ...NOTIFIED,
    labelKey: incidentReportsConstants.CASE_V2_NOTIFICATIONS_MANAGER,
    party: NotifiedPartyEnum.manager
  },
  {
    ...NOTIFIED,
    labelKey: incidentReportsConstants.CASE_V2_NOTIFICATIONS_FAMILY,
    party: NotifiedPartyEnum.familyNextOfKin
  },
  {
    ...NOTIFIED,
    hasReferenceNumber: true,
    labelKey: incidentReportsConstants.CASE_V2_NOTIFICATIONS_POLICE,
    party: NotifiedPartyEnum.police
  },
  {
    ...NOTIFIED,
    hasReferenceNumber: true,
    labelKey: incidentReportsConstants.CASE_V2_NOTIFICATIONS_LOCAL_AUTHORITY,
    party: NotifiedPartyEnum.localAuthority
  },
  {
    ...NOTIFIED,
    hasReferenceNumber: true,
    labelKey: incidentReportsConstants.CASE_V2_NOTIFICATIONS_CQC,
    party: NotifiedPartyEnum.cqc
  },
  {
    ...NOTIFIED,
    hasReferenceNumber: true,
    labelKey: incidentReportsConstants.CASE_V2_NOTIFICATIONS_RIDDOR,
    party: NotifiedPartyEnum.riddor
  },
  {
    byLabelKey: incidentReportsConstants.CASE_V2_NOTIFICATIONS_SUBMITTED_BY,
    dateLabelKey: incidentReportsConstants.CASE_V2_NOTIFICATIONS_SUBMITTED_DATE,
    hasEnquiry: true,
    hasReferenceNumber: true,
    labelKey: incidentReportsConstants.CASE_V2_NOTIFICATIONS_SAFEGUARDING,
    party: NotifiedPartyEnum.localAuthoritySafeguarding
  }
]

type PartyValues = {
  /** Unticked with its saved entries dropped; ticked again, it starts empty. */
  discarded?: boolean
  enquiryClosedAt?: Dayjs | null
  enquiryLocalAuthority?: string
  enquiryOutcome?: SafeguardingEnquiryOutcomeEnum
  enquiryOutcomeAndRecommendation?: string
  enquiryType?: string
  isNotified: boolean
  notifiedAt?: Dayjs | null
  notifiedBy?: string
  progressedToEnquiry?: boolean
  referenceNumber?: string
}

/** A party's detail fields, emptied. */
const NO_DETAIL: Omit<PartyValues, 'isNotified'> = {
  ...NO_ENQUIRY,
  notifiedAt: null,
  notifiedBy: undefined,
  progressedToEnquiry: undefined,
  referenceNumber: undefined
}

type NotificationsValues = Partial<Record<NotifiedPartyEnum, PartyValues>>

/** The detail fields a party shows; the rest of a saved entry is kept as is. */
const shownFields = ({ hasEnquiry, hasReferenceNumber }: PartyDefinition) => [
  'notifiedAt' as const,
  'notifiedBy' as const,
  ...(hasReferenceNumber ? ['referenceNumber' as const] : []),
  ...(hasEnquiry
    ? [
        'enquiryClosedAt' as const,
        'enquiryLocalAuthority' as const,
        'enquiryOutcome' as const,
        'enquiryOutcomeAndRecommendation' as const,
        'enquiryType' as const,
        'progressedToEnquiry' as const
      ]
    : [])
]

/**
 * The saved date, untouched, while the same day is picked: an entry captured
 * in the app may carry a time, which a date-only picker would otherwise drop.
 */
const toNotifiedAt = (picked: Dayjs, saved: string | undefined) =>
  saved && picked.format('YYYY-MM-DD') === saved.slice(0, 10)
    ? saved
    : (toDateOnlyIso(picked) ?? undefined)

/** The enquiry's answers, sent only while "Progressed to an enquiry" is ticked. */
const toEnquiry = (
  values: PartyValues
): Pick<
  NotifiedPartyValue,
  | 'enquiryClosedAt'
  | 'enquiryLocalAuthority'
  | 'enquiryOutcome'
  | 'enquiryOutcomeAndRecommendation'
  | 'enquiryType'
> =>
  withoutBlanks({
    enquiryClosedAt: toDateOnlyIso(values.enquiryClosedAt) ?? undefined,
    enquiryLocalAuthority: trimmed(values.enquiryLocalAuthority),
    enquiryOutcome: values.enquiryOutcome,
    enquiryOutcomeAndRecommendation: trimmed(
      values.enquiryOutcomeAndRecommendation
    ),
    enquiryType: trimmed(values.enquiryType)
  })

const toEntry = (
  definition: PartyDefinition,
  values: PartyValues,
  saved: NotifiedPartyValue | undefined
): NotifiedPartyValue => {
  const fields = shownFields(definition)
  const kept = Object.fromEntries(
    Object.entries(saved ?? {}).filter(
      ([key]) => !(fields as ReadonlyArray<string>).includes(key)
    )
  )
  const referenceNumber = values.referenceNumber?.trim()

  return {
    ...kept,
    party: definition.party,
    ...(values.notifiedAt
      ? { notifiedAt: toNotifiedAt(values.notifiedAt, saved?.notifiedAt) }
      : {}),
    ...(values.notifiedBy ? { notifiedBy: values.notifiedBy } : {}),
    ...(definition.hasReferenceNumber && referenceNumber
      ? { referenceNumber }
      : {}),
    ...(definition.hasEnquiry && values.progressedToEnquiry !== undefined
      ? { progressedToEnquiry: values.progressedToEnquiry }
      : {}),
    ...(definition.hasEnquiry && values.progressedToEnquiry
      ? toEnquiry(values)
      : {})
  }
}

/**
 * Step 9: who was notified. Each party is a tick that reveals when and by whom,
 * and more for some parties. The form edits a party's first saved entry and
 * keeps any others; entries owned only by Immediate actions are not sent.
 */
const NotificationsStep = ({
  incidentReport,
  handleRef,
  onDirtyChange
}: IncidentCaseV2StepProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  const saved = useMemo(
    () => savedNotifiedParties(incidentReport),
    [incidentReport]
  )
  const savedEntries = (party: NotifiedPartyEnum) =>
    saved.filter((entry) => entry.party === party)
  const loggedById = incidentReport.log.loggedBy?.id
  const teamMemberOptions = useTeamMemberOptions({
    saved: [...saved.map((entry) => entry.notifiedBy), loggedById]
  })

  // Until the step is first saved, the manager starts ticked, told by whoever
  // logged the incident on the day the case opened, as agreed: they nearly
  // always are, so the user only corrects it.
  const isSaved = Boolean(
    (
      incidentReport.case as { completedSteps?: Array<string> } | null
    )?.completedSteps?.includes('notifications')
  )
  // Not yet on the report when the case opened during this visit.
  const { caseOpenedAt } = incidentReport as { caseOpenedAt?: string | null }

  const initialValues = useMemo<NotificationsValues>(
    () =>
      Object.fromEntries(
        PARTIES.map(({ party }) => {
          const entry = saved.find((savedParty) => savedParty.party === party)
          const isPrefilled =
            !isSaved && !entry && party === NotifiedPartyEnum.manager
          return [
            party,
            {
              enquiryClosedAt: fromDateOnlyIso(entry?.enquiryClosedAt),
              enquiryLocalAuthority: entry?.enquiryLocalAuthority,
              enquiryOutcome: entry?.enquiryOutcome,
              enquiryOutcomeAndRecommendation:
                entry?.enquiryOutcomeAndRecommendation,
              enquiryType: entry?.enquiryType,
              isNotified: entry !== undefined || isPrefilled,
              notifiedAt: isPrefilled
                ? dayjs(caseOpenedAt ?? undefined)
                : fromDateOnlyIso(entry?.notifiedAt),
              notifiedBy: isPrefilled ? loggedById : entry?.notifiedBy,
              progressedToEnquiry: entry?.progressedToEnquiry,
              referenceNumber: entry?.referenceNumber
            }
          ]
        })
      ),
    [caseOpenedAt, isSaved, loggedById, saved]
  )

  const toStepValues = (
    values: NotificationsValues
  ): IncidentCaseV2StepValues => ({
    // Only this step's parties: the server keeps the rest, so another step's
    // entry it would reject (an unnamed "other") can't fail this save.
    notifiedParties: PARTIES.flatMap((definition) => {
      const partyValues = values[definition.party]
      if (!partyValues?.isNotified) {
        return []
      }
      const [first, ...others] = partyValues.discarded
        ? []
        : savedEntries(definition.party)
      return [toEntry(definition, partyValues, first), ...others]
    })
  })

  const placeholder = t(incidentReportsConstants.CASE_V2_SELECT_PLACEHOLDER)

  return (
    <IncidentCaseV2StepForm
      handleRef={handleRef}
      onDirtyChange={onDirtyChange}
      initialValues={initialValues}
      toStepValues={toStepValues}
      testId={testId}
    >
      <StepSection>
        {PARTIES.map((definition) => {
          const { party } = definition
          const partyTestId = `${testId}-${kebabCase(party)}`

          return (
            <StepRevealGroup key={party}>
              <AntdForm.Item
                name={[party, 'isNotified']}
                valuePropName="checked"
              >
                <StepCheckbox
                  confirmUntick={{
                    body: t(
                      incidentReportsConstants.CASE_V2_NOTIFICATIONS_DISCARD_BODY
                    ),
                    // Saved entries carry app free text this step doesn't show.
                    isNeeded: (form) => {
                      const [first, ...others] = form.getFieldValue([
                        party,
                        'discarded'
                      ])
                        ? []
                        : savedEntries(party)
                      return [
                        { ...first, ...form.getFieldValue(party) },
                        ...others
                      ].some(hasNotifiedPartyDetail)
                    },
                    onConfirm: (form) =>
                      form.setFieldValue(party, {
                        ...NO_DETAIL,
                        discarded: true,
                        isNotified: true
                      }),
                    title: t(
                      incidentReportsConstants.CASE_V2_NOTIFICATIONS_DISCARD_TITLE
                    )
                  }}
                  testId={partyTestId}
                >
                  {t(definition.labelKey)}
                </StepCheckbox>
              </AntdForm.Item>

              <AntdForm.Item noStyle dependencies={[[party, 'isNotified']]}>
                {({ getFieldValue }) =>
                  getFieldValue([party, 'isNotified']) && (
                    <StepReveal data-testid={`${partyTestId}-details`}>
                      <StepField
                        name={[party, 'notifiedAt']}
                        label={t(definition.dateLabelKey)}
                      >
                        <DatePicker
                          data-testid={`${partyTestId}-notified-at`}
                          format={DATE_FORMAT}
                        />
                      </StepField>
                      <StepField
                        name={[party, 'notifiedBy']}
                        label={t(definition.byLabelKey)}
                      >
                        <Select
                          data-testid={`${partyTestId}-notified-by`}
                          allowClear
                          optionFilterProp="label"
                          options={teamMemberOptions}
                          placeholder={placeholder}
                          showSearch
                        />
                      </StepField>
                      {definition.hasReferenceNumber && (
                        <StepField
                          name={[party, 'referenceNumber']}
                          label={t(
                            incidentReportsConstants.CASE_V2_NOTIFICATIONS_REFERENCE_NUMBER
                          )}
                        >
                          <Input
                            data-testid={`${partyTestId}-reference-number`}
                          />
                        </StepField>
                      )}
                      {definition.hasEnquiry && (
                        <SafeguardingEnquiryFields
                          party={party}
                          testId={partyTestId}
                        />
                      )}
                    </StepReveal>
                  )
                }
              </AntdForm.Item>
            </StepRevealGroup>
          )
        })}
      </StepSection>
    </IncidentCaseV2StepForm>
  )
}

export default NotificationsStep
